import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Funnel, X, CalendarDots } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const STATUS_LABELS = {
  new: "New", contacted: "Contacted", in_progress: "In Progress",
  qualified: "Qualified", won: "Won", lost: "Lost",
};

/**
 * Shared filter panel for whole-app scoping.
 *
 * Renders only the inputs that make sense for the current user's role
 * (admin sees Departments + Users; manager sees scoped Departments + scoped
 * Users; user sees only date + time-to-close because their assignment scope
 * is already enforced server-side).
 *
 * Controlled component. The parent owns the filter object and persists it
 * (e.g. via URL search params) if it wants reload-stable filters.
 *
 *   value = { date_from, date_to, departments[], assignees[],
 *             statuses[], sources[], min_days_to_close, max_days_to_close }
 *
 * onApply is called only when the user clicks "Apply" — letting them stage
 * several changes before re-querying.
 */
export default function FilterBar({
  value,
  onApply,
  options,            // from /api/reports/options
  showStatuses = true,
  showSources = true,
  showTimeToClose = true,
  showDates = true,
  showJobTitles = false,
  testId = "filter-bar",
}) {
  const [draft, setDraft] = useState(value);
  const [presets, setPresets] = useState(null); // { this_quarter, last_quarter, this_fiscal_year, last_fiscal_year, quarters_current_fy }

  useEffect(() => { setDraft(value); }, [value]);

  // Fetch fiscal-year quarter presets once — cheap and lets Analytics/Reports/Leads all show the same picker.
  useEffect(() => {
    if (!showDates) return;
    let cancelled = false;
    api.get("/settings/general")
      .then((r) => { if (!cancelled) setPresets(r.data?.quarter_presets || null); })
      .catch((e) => console.debug("filter presets fetch failed:", e?.response?.status || e?.message));
    return () => { cancelled = true; };
  }, [showDates]);

  const applyPreset = (preset) => {
    if (!preset) return;
    const next = { ...draft, date_from: preset.start, date_to: preset.end };
    setDraft(next);
    onApply(next);
  };

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const toggleMulti = (k, v) => setDraft((d) => {
    const cur = d[k] || [];
    return { ...d, [k]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
  });

  const reset = () => {
    const cleared = {
      date_from: "", date_to: "",
      departments: [], assignees: [],
      statuses: [], sources: [], job_titles: [],
      min_days_to_close: "", max_days_to_close: "",
    };
    setDraft(cleared);
    onApply(cleared);
  };

  const apply = () => onApply(draft);

  const activeCount = (
    (draft.departments?.length || 0) +
    (draft.assignees?.length || 0) +
    (draft.statuses?.length || 0) +
    (draft.sources?.length || 0) +
    (draft.job_titles?.length || 0) +
    (draft.date_from ? 1 : 0) +
    (draft.date_to ? 1 : 0) +
    (draft.min_days_to_close !== "" && draft.min_days_to_close != null ? 1 : 0) +
    (draft.max_days_to_close !== "" && draft.max_days_to_close != null ? 1 : 0)
  );

  const role = options?.role || "user";
  const showDept = role !== "user" && (options?.departments?.length || 0) > 0;
  const showUsers = role !== "user" && (options?.users?.length || 0) > 0;

  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-2 label-tracking">
          <Funnel size={12} /> Filters
          {activeCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-slate-900 text-white text-[10px]">{activeCount}</span>
          )}
        </div>
        <div className="inline-flex gap-2">
          {activeCount > 0 && (
            <button
              type="button"
              data-testid="filter-reset"
              onClick={reset}
              className="text-[11px] inline-flex items-center gap-1 text-slate-500 hover:text-slate-900"
            >
              <X size={11} /> Reset
            </button>
          )}
          <Button
            type="button"
            size="sm"
            data-testid="filter-apply"
            onClick={apply}
            className="rounded-sm h-8 text-xs bg-slate-900 hover:bg-slate-800"
          >
            Apply
          </Button>
        </div>
      </div>

      {showDates && presets && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="filter-quarter-presets">
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 mr-1">
            <CalendarDots size={12} /> Quick:
          </span>
          <PresetBtn testId="preset-this-quarter" onClick={() => applyPreset(presets.this_quarter)}>{presets.this_quarter?.label}</PresetBtn>
          <PresetBtn testId="preset-last-quarter" onClick={() => applyPreset(presets.last_quarter)}>{presets.last_quarter?.label}</PresetBtn>
          <PresetBtn testId="preset-this-fy" onClick={() => applyPreset(presets.this_fiscal_year)}>{presets.this_fiscal_year?.label}</PresetBtn>
          <PresetBtn testId="preset-last-fy" onClick={() => applyPreset(presets.last_fiscal_year)}>{presets.last_fiscal_year?.label}</PresetBtn>
          <span className="mx-1 h-3 w-px bg-slate-200" />
          {(presets.quarters_current_fy || []).map((q) => (
            <PresetBtn key={q.key} testId={`preset-${q.key.toLowerCase()}`} onClick={() => applyPreset(q)}>{q.key}</PresetBtn>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {showDates && (
          <>
            <div>
              <Label className="label-tracking">From</Label>
              <Input
                data-testid="filter-date-from"
                type="date"
                value={draft.date_from || ""}
                onChange={(e) => set("date_from", e.target.value)}
                className="mt-1.5 rounded-sm h-9 text-xs"
              />
            </div>
            <div>
              <Label className="label-tracking">To</Label>
              <Input
                data-testid="filter-date-to"
                type="date"
                value={draft.date_to || ""}
                onChange={(e) => set("date_to", e.target.value)}
                className="mt-1.5 rounded-sm h-9 text-xs"
              />
            </div>
          </>
        )}
        {showStatuses && (
          <MultiChips
            label="Status"
            testIdPrefix="status"
            items={(options?.statuses || []).map((s) => ({ value: s, label: STATUS_LABELS[s] || s }))}
            selected={draft.statuses || []}
            onToggle={(v) => toggleMulti("statuses", v)}
          />
        )}
        {showSources && (
          <SelectMulti
            label="Sources"
            testId="filter-sources"
            items={(options?.sources || []).map((s) => ({ value: s, label: s }))}
            selected={draft.sources || []}
            onToggle={(v) => toggleMulti("sources", v)}
          />
        )}
        {showDept && (
          <SelectMulti
            label="Departments"
            testId="filter-departments"
            items={(options?.departments || []).map((d) => ({ value: d, label: d }))}
            selected={draft.departments || []}
            onToggle={(v) => toggleMulti("departments", v)}
          />
        )}
        {showUsers && (
          <SelectMulti
            label="Users"
            testId="filter-users"
            items={(options?.users || []).map((u) => ({ value: u.email, label: u.name || u.email }))}
            selected={draft.assignees || []}
            onToggle={(v) => toggleMulti("assignees", v)}
          />
        )}
        {showJobTitles && (
          <SelectMulti
            label="Job title"
            testId="filter-job-titles"
            items={(options?.job_titles || []).map((t) => ({ value: t, label: t }))}
            selected={draft.job_titles || []}
            onToggle={(v) => toggleMulti("job_titles", v)}
          />
        )}
        {showTimeToClose && (
          <div className="sm:col-span-2 lg:col-span-2">
            <Label className="label-tracking">Time to close (days)</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input
                data-testid="filter-min-days"
                type="number"
                min="0"
                placeholder="min"
                value={draft.min_days_to_close ?? ""}
                onChange={(e) => set("min_days_to_close", e.target.value === "" ? "" : Number(e.target.value))}
                className="h-9 rounded-sm text-xs"
              />
              <span className="text-slate-400 text-xs">to</span>
              <Input
                data-testid="filter-max-days"
                type="number"
                min="0"
                placeholder="max"
                value={draft.max_days_to_close ?? ""}
                onChange={(e) => set("max_days_to_close", e.target.value === "" ? "" : Number(e.target.value))}
                className="h-9 rounded-sm text-xs"
              />
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Filters to closed leads (Won/Lost) within range.</p>
          </div>
        )}
      </div>

      {(draft.statuses?.length > 0 || draft.sources?.length > 0
        || draft.departments?.length > 0 || draft.assignees?.length > 0
        || draft.job_titles?.length > 0) && (
        <div className="flex flex-wrap gap-1 pt-1 border-t border-slate-100">
          {[
            ...(draft.statuses || []).map((v) => ({ k: "statuses", v, label: STATUS_LABELS[v] || v })),
            ...(draft.sources || []).map((v) => ({ k: "sources", v, label: v })),
            ...(draft.departments || []).map((v) => ({ k: "departments", v, label: v })),
            ...(draft.assignees || []).map((v) => ({ k: "assignees", v, label: (options?.users || []).find((u) => u.email === v)?.name || v })),
            ...(draft.job_titles || []).map((v) => ({ k: "job_titles", v, label: v })),
          ].map((chip) => (
            <button
              key={`${chip.k}:${chip.v}`}
              type="button"
              data-testid={`filter-chip-${chip.k}-${chip.v}`}
              onClick={() => toggleMulti(chip.k, chip.v)}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm bg-slate-900 text-white hover:bg-slate-700"
              title="Click to remove"
            >
              {chip.label} <X size={10} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SelectMulti({ label, items, selected, onToggle, testId }) {
  return (
    <div>
      <Label className="label-tracking">{label}</Label>
      <Select onValueChange={onToggle}>
        <SelectTrigger data-testid={testId} className="mt-1.5 rounded-sm h-9 text-xs">
          <SelectValue placeholder={selected.length ? `${selected.length} selected` : `All ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {items.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-slate-400">No options</div>
          )}
          {items.map((it) => (
            <SelectItem key={it.value} value={it.value} className="text-xs">
              <span className="inline-flex items-center gap-2">
                <span className={`inline-block h-3 w-3 rounded-sm border ${selected.includes(it.value) ? "bg-slate-900 border-slate-900" : "border-slate-300"}`} />
                {it.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function MultiChips({ label, items, selected, onToggle, testIdPrefix }) {
  return (
    <div>
      <Label className="label-tracking">{label}</Label>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {items.map((it) => {
          const on = selected.includes(it.value);
          return (
            <button
              key={it.value}
              type="button"
              data-testid={`${testIdPrefix}-chip-${it.value}`}
              onClick={() => onToggle(it.value)}
              className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${
                on
                  ? "bg-slate-900 border-slate-900 text-white"
                  : "border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const emptyFilters = () => ({
  date_from: "", date_to: "",
  departments: [], assignees: [],
  statuses: [], sources: [], job_titles: [],
  min_days_to_close: "", max_days_to_close: "",
});

function PresetBtn({ children, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="px-2 py-0.5 rounded-sm text-[11px] border border-slate-200 bg-white hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors"
    >
      {children}
    </button>
  );
}

/** Convert a filter state object into query-string params suitable for GET endpoints. */
export function filtersToParams(f) {
  const p = {};
  if (f.date_from) p.date_from = f.date_from;
  if (f.date_to) p.date_to = f.date_to;
  if (f.departments?.length) p.departments = f.departments.join(",");
  if (f.assignees?.length) p.assignees = f.assignees.join(",");
  if (f.statuses?.length) p.statuses = f.statuses.join(",");
  if (f.sources?.length) p.sources = f.sources.join(",");
  if (f.job_titles?.length) p.job_titles = f.job_titles.join(",");
  if (f.min_days_to_close !== "" && f.min_days_to_close != null) p.min_days_to_close = f.min_days_to_close;
  if (f.max_days_to_close !== "" && f.max_days_to_close != null) p.max_days_to_close = f.max_days_to_close;
  return p;
}
